export function getNotRequested(): NotRequested {
  return {
    kind: 'not-requested',
  };
}

export function getRequesting(): Requesting {
  return {
    kind: 'requesting',
  };
}

export function getSuccess<T>(data: T): Success<T> {
  return {
    data,
    kind: 'success',
  };
}

export function getFailed<E>(error: E): Failed<E> {
  return {
    error,
    kind: 'failed',
  };
}

export function getRefreshing<T, E>(prevData: Refreshable<T, E>): Refreshing<T, E> {
  return {
    prevData,
    kind: 'refreshing',
  };
}

// PrevData would be a refreshable
export function getRefreshSuccess<T, E>(
  prevData: Refreshable<T, E>,
  data: T,
): RefreshSuccess<T, E> {
  return {
    data,
    prevData,
    kind: 'refresh-success',
  };
}

export function getRefreshFailed<T, E>(prevData: Refreshable<T, E>, error: E): RefreshFailed<T, E> {
  return {
    error,
    prevData,
    kind: 'refresh-failed',
  };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NotRequestedHandler = () => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestingHandler = () => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SuccessHandler<T> = (data: T) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FailedHandler<E> = (error: E) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshHandler<T, E> = (prevData: Refreshable<T, E>) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshSuccessHandler<T, E> = (prevData: Refreshable<T, E>, newData: T) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshFailedHandler<T, E> = (prevData: Refreshable<T, E>, error: E) => any;

export function handleRequestable<T, E>(
  onNotRequested: NotRequestedHandler,
  onRequesting: RequestingHandler,
  onSuccess: SuccessHandler<T>,
  onFailed: FailedHandler<E>,
) {
  return (
    resource: Requestable<T, E>,
  ): ReturnType<NotRequestedHandler | RequestingHandler | SuccessHandler<T> | FailedHandler<E>> => {
    switch (resource.kind) {
      case 'not-requested':
        return onNotRequested();
      case 'requesting':
        return onRequesting();
      case 'success':
        return onSuccess(resource.data);
      case 'failed':
        return onFailed(resource.error);
    }
  };
}

export function extractRequestable<T, E>(resource: Requestable<T, E>): Maybe<T> {
  return resource.kind === 'success' ? resource.data : null;
}

export function extractRequestableError<T, E>(resource: Requestable<T, E>): Maybe<E> {
  return resource.kind === 'failed' ? resource.error : null;
}

export function handleRefreshable<T, E>(
  onNotRequested: NotRequestedHandler,
  onRefresh: RefreshHandler<T, E>,
  onSuccess: RefreshSuccessHandler<T, E>,
  onFailed: RefreshFailedHandler<T, E>,
) {
  return (
    resource: Refreshable<T, E>,
  ): ReturnType<
    | NotRequestedHandler
    | RefreshHandler<T, E>
    | RefreshSuccessHandler<T, E>
    | RefreshFailedHandler<T, E>
  > => {
    switch (resource.kind) {
      case 'not-requested':
        return onNotRequested();
      case 'refreshing':
        return onRefresh(resource.prevData);
      case 'refresh-success':
        return onSuccess(resource.prevData, resource.data);
      case 'refresh-failed':
        return onFailed(resource.prevData, resource.error);
    }
  };
}

export function extractRefreshable<T, E>(resource: Refreshable<T, E>): Maybe<T> {
  return handleRefreshable<T, E>(
    () => null,
    (prevData) => (prevData.kind !== 'not-requested' ? extractRefreshable<T, E>(prevData) : null),
    (_, data) => data,
    () => null,
  )(resource);
}

// extract the current value from the Refreshable as above, but in the case of failure, return the last known value
export function extractRefreshableLastValue<T, E>(resource: Refreshable<T, E>): Maybe<T> {
  return handleRefreshable<T, E>(
    () => null,
    (prevData) => {
      return prevData.kind !== 'not-requested' ? extractRefreshableLastValue<T, E>(prevData) : null;
    },
    (_, data) => data,
    (prevData) => {
      return prevData.kind !== 'not-requested' ? extractRefreshableLastValue<T, E>(prevData) : null;
    },
  )(resource);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function requestableHasFailed(requestable: Requestable<any, any>): boolean {
  return requestable.kind === 'failed';
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function requestableHasSucceeded(requestable: Requestable<any, any>): boolean {
  return requestable.kind === 'success';
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function requestableIsLoading(requestable: Requestable<any, any>): boolean {
  return requestable.kind === 'requesting';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function requestableWasNotRequested(requestable: Requestable<any, any>): boolean {
  return requestable.kind === 'not-requested';
}
